import { useState, useEffect, useRef, type MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, X, type LucideIcon } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { admin } from '../../api/admin';
import { auth as authApi } from '../../api/auth';
import { getCompanyProfileShared } from '../../api/companyProfile';
import { setCompanyDateFormat, setCompanyTimeFormat, setCompanyTimezone } from '../../utils/date';
import {
  TENANT_GROUPS,
  PLATFORM_GROUPS,
  resolveGroups,
  isItemActive,
  type NavVisibility,
  type ResolvedEntry,
} from '../../config/navigation';
import { setDownloadPrompt } from '../../utils/trackingDocuments';

/** Keep in step with the page offset in Layout.tsx (`lg:ml-[88px]`). */
const RAIL_WIDTH = 'w-[88px]';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Ctrl/Cmd/Shift/Alt-click opens the page elsewhere (new tab or window), so
 *  the menu should stay open where it is. */
const opensElsewhere = (e: MouseEvent) => e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;

/**
 * A group's entries split by `section` for the flyout list: sections in
 * first-seen order, the pages inside each sorted A-Z by label. Display only -
 * the config order still decides landing order (firstVisibleItem).
 */
const sectionsOf = (items: ResolvedEntry[]) => {
  const columns: { title?: string; items: ResolvedEntry[] }[] = [];
  for (const item of items) {
    const title = item.entry.section;
    let column = columns.find((c) => c.title === title);
    if (!column) {
      column = { title, items: [] };
      columns.push(column);
    }
    column.items.push(item);
  }
  for (const column of columns) {
    column.items.sort((a, b) => a.entry.label.localeCompare(b.entry.label, undefined, { sensitivity: 'base' }));
  }
  return columns;
};

/**
 * Icon rail plus a flyout panel per group.
 *
 * The rail holds one button per group, so it never scrolls whatever the
 * screen height; Settings is pinned at its foot. Clicking a group opens its
 * pages in a narrow panel beside the rail, one list under small section
 * headings; picking a page, Esc, or a click outside closes it. A
 * group with a single entry for this user (Overview, or a role that sees one
 * page of a group) links straight to it instead.
 *
 * Only the drawing changed: the same resolved groups decide what shows, and
 * the route guards and API still check every page.
 */
const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const { user, updateUser, hasFeature, hasMenuPermission, hasAction, can } = useAuth();
  const location = useLocation();

  const [systemLogo, setSystemLogo] = useState<string | null>(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  // The group shown in the panel. Kept after closing, so the panel slides away
  // with its contents instead of emptying first.
  const [panelGroupId, setPanelGroupId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const railButtons = useRef<Record<string, HTMLButtonElement | null>>({});

  const getLogoUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.magento.local/v1/admin';
      const origin = new URL(baseUrl).origin;
      return `${origin}${path}`;
    } catch {
      return path;
    }
  };

  // The system logo is only a fallback, so skip the request when the company's
  // own logo (persisted with the user) is already known.
  const companyLogo = user?.companyLogo;
  useEffect(() => {
    if (companyLogo || systemLogo) return;
    admin
      .getPublicSettings()
      .then((data) => {
        if (data && data.logo) setSystemLogo(data.logo);
      })
      .catch(() => {});
  }, [companyLogo]);

  // The role's menu list is re-read on every load: the one in the browser is
  // from login time, and a role edited since then would otherwise keep showing
  // (and opening) pages the API now refuses.
  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.role !== 'company_admin') {
      authApi
        .getProfile()
        .then((profile: any) => {
          if (profile && Array.isArray(profile.rolePermissions)) {
            updateUser({
            rolePermissions: profile.rolePermissions,
            roleName: profile.roleName,
            // Facility assignments change on the Facilities page while people
            // are signed in; the copy from login would keep confining (or not
            // confining) them to facilities they no longer have.
            assignedWarehouseIds: Array.isArray(profile.assignedWarehouseIds) ? profile.assignedWarehouseIds : [],
          });
          }
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      getCompanyProfileShared()
        .then((profile: any) => {
          if (profile) {
            updateUser({
              companyLogo: profile.logo,
              companyName: profile.name,
              // Always settle to a list: NavGuard waits while this is unset.
              activeFeatures: Array.isArray(profile.activeFeatures) ? profile.activeFeatures : [],
              activePlanName: profile.activePlanName ?? null,
              noPlanNeed: !!profile.noPlanNeed,
            });
          }
          setCompanyDateFormat(profile?.dateFormat);
          setCompanyTimeFormat(profile?.timeFormat);
          setCompanyTimezone(profile?.timezone);
          setDownloadPrompt(profile?.autoBatchSettings?.downloadPrompt);
        })
        // Keep the last known plan; with none at all, fail closed to "no
        // features" so guarded pages stop waiting.
        .catch(() => {
          if (!Array.isArray(user?.activeFeatures)) updateUser({ activeFeatures: [] });
        });
    }
  }, []);

  // Until the plan arrives on a fresh login, draw items as available instead
  // of flashing lock icons. Display only - NavGuard and the API still check.
  const planPending = user?.role !== 'super_admin' && !Array.isArray(user?.activeFeatures);
  const ctx = { can, hasFeature, hasMenuPermission, hasAction, planExempt: !!user?.noPlanNeed || planPending };
  const source = can('platform.admin') ? PLATFORM_GROUPS : TENANT_GROUPS;
  const groups = resolveGroups(source, ctx);

  // The one entry that owns the current page. First match wins, in sidebar
  // order, so exactly one entry is highlighted. A hub owns every tab's page.
  const activeEntryId =
    groups
      .flatMap((g) => g.items)
      .find(
        ({ pages, visibility }) =>
          visibility === 'visible' && pages.some((page) => isItemActive(page, location.pathname)),
      )?.entry.id ?? null;
  const activeGroupId =
    groups.find((g) => g.items.some(({ entry }) => entry.id === activeEntryId))?.id ?? null;

  const panelOpen = groups.some((g) => g.id === openGroupId);
  const panelGroup = groups.find((g) => g.id === panelGroupId) ?? null;

  const openPanel = (id: string) => {
    setPanelGroupId(id);
    setOpenGroupId(id);
  };

  const closePanel = (restoreFocus: boolean) => {
    const id = openGroupId;
    setOpenGroupId(null);
    if (restoreFocus && id) railButtons.current[id]?.focus();
  };

  // On small screens the panel belongs to the drawer: closing the drawer
  // (overlay, Esc, the X) closes the panel with it.
  const drawerWasOpen = useRef(isOpen);
  useEffect(() => {
    if (drawerWasOpen.current && !isOpen) setOpenGroupId(null);
    drawerWasOpen.current = isOpen;
  }, [isOpen]);

  // Any navigation closes the panel and, on small screens, the drawer.
  useEffect(() => {
    onClose();
    setOpenGroupId(null);
  }, [location.pathname]);

  // An open panel takes focus (first page) and closes on Esc.
  useEffect(() => {
    if (!openGroupId) return;
    panelRef.current?.querySelector<HTMLElement>('a')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openGroupId]);

  const renderRailItem = (group: (typeof groups)[number]) => {
    const only = group.items.length === 1 ? group.items[0] : null;
    const label = group.label ?? only?.entry.label ?? '';
    const Icon = group.icon ?? only?.entry.icon;
    const current = group.id === activeGroupId;
    // Open panel = inverted (white), so it never looks like the group
    // you are in (faint white) while both are highlighted.
    const railClass = `flex w-full flex-col items-center gap-1 rounded-xl px-1 py-2.5 text-xs font-medium transition-colors ${
      openGroupId === group.id
        ? 'bg-white text-slate-900'
        : current
          ? 'bg-white/15 text-white'
          : 'text-white/80 hover:bg-white/10 hover:text-white'
    }`;
    const content = (
      <>
        {Icon && <Icon size={22} aria-hidden="true" />}
        <span className="w-full truncate text-center">{label}</span>
      </>
    );

    // One entry: no panel, straight to it (or to the plan page if locked).
    if (only) {
      return (
        <Link
          key={group.id}
          to={only.visibility === 'locked' ? '/settings/subscription' : only.path}
          title={only.visibility === 'locked' ? `${label} is not included in your current plan` : label}
          aria-current={current ? 'page' : undefined}
          // Close any open panel here, not only on a page change: clicking the
          // page you are already on changes nothing, and the click looked dead.
          onClick={(e) => {
            if (opensElsewhere(e)) return;
            setOpenGroupId(null);
            onClose();
          }}
          className={railClass}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        key={group.id}
        ref={(el) => {
          railButtons.current[group.id] = el;
        }}
        type="button"
        aria-expanded={openGroupId === group.id}
        aria-controls="nav-panel"
        onClick={() => (openGroupId === group.id ? closePanel(false) : openPanel(group.id))}
        className={railClass}
      >
        {content}
      </button>
    );
  };

  return (
    <>
      <button
        type="button"
        aria-label="Close navigation menu"
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-slate-950/45 backdrop-blur-[2px] transition-opacity duration-[400ms] motion-reduce:transition-none lg:hidden print:hidden ${
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      <aside
        id="primary-navigation"
        aria-label="Primary navigation"
        className={`fixed inset-y-0 left-0 z-50 flex h-dvh ${RAIL_WIDTH} flex-col border-r border-black/10 bg-sidebar shadow-xl transition-transform duration-[400ms] ease-out motion-reduce:transition-none lg:visible lg:z-40 lg:translate-x-0 lg:shadow-sm print:hidden ${
          isOpen ? 'visible translate-x-0' : 'invisible -translate-x-full'
        }`}
      >
        <div className="flex flex-col items-center gap-2 px-2 pb-3 pt-4">
          {/* Uploaded logos are made for white backgrounds; on the dark rail
              they sit on a white tile or their dark parts disappear. */}
          {/* The logo goes home: "/" is the Dashboard, or the first page the
              role has when it has no Dashboard. Closes any open panel, like the
              rail's direct links. */}
          <Link
            to="/"
            aria-label="Go to dashboard"
            title="Dashboard"
            onClick={(e) => {
              if (opensElsewhere(e)) return;
              setOpenGroupId(null);
              onClose();
            }}
            className="flex w-full justify-center rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            {(user?.companyLogo || systemLogo) && !logoFailed ? (
              <div className="flex h-12 w-full items-center justify-center rounded-lg bg-white p-1.5">
                <img
                  src={getLogoUrl((user?.companyLogo || systemLogo) as string)}
                  alt={user?.companyLogo ? user.companyName || 'Company Logo' : 'System Logo'}
                  className="max-h-9 max-w-full object-contain"
                  onError={() => setLogoFailed(true)}
                />
              </div>
            ) : (
              <span className="text-lg font-bold text-white" title="Carrier Monitor">
                CM
              </span>
            )}
          </Link>
        </div>

        <nav aria-label="Main" className="sidebar-scroll flex-1 space-y-1 overflow-y-auto overscroll-contain px-2 pb-4">
          {groups.filter((group) => !group.pinned).map(renderRailItem)}
        </nav>

        {/* Setup, apart from the daily work: pinned to the bottom of the rail. */}
        {groups.some((group) => group.pinned) && (
          <div className="space-y-1 border-t border-white/10 px-2 py-3">
            {groups.filter((group) => group.pinned).map(renderRailItem)}
          </div>
        )}
      </aside>

      {/* Like Magento's admin menu: the panel slides out from behind the rail
          and back, while the page dims in and out. Stacking, low to high:
          top bar (30) < dimmer < panel < rail, so the panel moves under the
          rail; on phones everything sits above the drawer's own overlay (40). */}
      {groups.length > 0 && (
        <>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Close menu"
            aria-hidden={!panelOpen}
            onClick={() => closePanel(false)}
            className={`fixed inset-y-0 left-[88px] right-0 z-[42] cursor-default bg-slate-950/20 transition-[opacity,visibility] duration-[400ms] ease-out motion-reduce:transition-none lg:z-[32] print:hidden ${
              panelOpen ? 'visible opacity-100' : 'invisible opacity-0'
            } ${isOpen ? '' : 'max-lg:hidden'}`}
          />
          <div
            id="nav-panel"
            ref={panelRef}
            role="dialog"
            aria-label={panelGroup?.label ?? 'Menu'}
            aria-hidden={!panelOpen}
            inert={!panelOpen}
            className={`fixed inset-y-0 left-[88px] z-[45] w-[calc(100vw-88px)] overflow-y-auto border-l border-white/10 bg-sidebar px-3 pb-6 pt-6 text-white shadow-2xl transition-[transform,visibility] duration-[400ms] ease-out motion-reduce:transition-none sm:w-60 lg:z-[35] print:hidden ${
              panelOpen ? 'visible translate-x-0' : 'invisible -translate-x-full'
            } ${isOpen ? '' : 'max-lg:hidden'}`}
          >
            <div className="mb-5 flex items-center justify-between gap-3 pl-3">
              <h2 className="text-xl font-semibold">{panelGroup?.label}</h2>
              <button
                type="button"
                onClick={() => closePanel(true)}
                aria-label={`Close ${panelGroup?.label ?? ''} menu`}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white/70 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-5">
              {sectionsOf(panelGroup?.items ?? []).map((column) => (
                <div key={column.title ?? 'pages'}>
                  {column.title && (
                    <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-white/70">
                      {column.title}
                    </p>
                  )}
                  <div className="space-y-1">
                    {column.items.map(({ entry, visibility, path }) => (
                      <NavLink
                        key={entry.id}
                        label={entry.label}
                        icon={entry.icon}
                        to={path}
                        visibility={visibility}
                        isActive={entry.id === activeEntryId}
                        onNavigate={(e) => {
                          if (opensElsewhere(e)) return;
                          setOpenGroupId(null);
                          onClose();
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};

const NavLink = ({
  label,
  icon: Icon,
  to,
  visibility,
  isActive,
  onNavigate,
}: {
  label: string;
  icon: LucideIcon;
  to: string;
  visibility: NavVisibility;
  isActive: boolean;
  /** Closes the panel even when the link is the page already open. */
  onNavigate: (e: MouseEvent) => void;
}) => {
  if (visibility === 'locked') {
    return (
      <Link
        to="/settings/subscription"
        onClick={onNavigate}
        title={`${label} is not included in your current plan`}
        className="flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer transition-colors text-white/70 hover:text-white/90 hover:bg-white/5"
      >
        <Icon size={18} />
        <span className="font-medium text-sm truncate">{label}</span>
        <Lock size={14} className="ml-auto shrink-0" />
      </Link>
    );
  }

  return (
    <Link
      to={to}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
      className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer transition-colors ${
        isActive
          ? 'bg-white/15 text-white border border-white/15'
          : 'text-white hover:bg-white/10'
      }`}
    >
      <Icon size={18} />
      <span className="font-medium text-sm">{label}</span>
    </Link>
  );
};

export default Sidebar;
